import type { components } from "@/src/api/api";

/** Erreurs de validation par champ, telles que renvoyées par le serveur. */
export type ApiErrorData =
  components["schemas"]["ErrorResponseSchema"]["errorData"];

/**
 * Erreur d'API normalisée — portage à l'identique de celle de wargame-app.
 *
 * Tout ce que lève la couche API est une `ApiError` : une vraie `Error`, donc
 * avec une `stack`, un `instanceof` fiable, et un message lisible en log.
 *
 * ⚠️ `errorData` vit dans **`body.errorData`**, pas à la racine de l'erreur.
 * C'est le piège qui, côté app, avait fait disparaître les erreurs de
 * validation par champ de deux formulaires.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly statusText: string;
  /** Corps de la réponse, tel que parsé (peut être `undefined` si illisible). */
  readonly body: unknown;
  /** Code métier du serveur (`body.error`), ex. `"INVALID_CREDENTIALS"`. */
  readonly code: string | undefined;
  /** Erreurs par champ (`body.errorData`), présentes sur les 400 de validation. */
  readonly errorData: ApiErrorData;

  constructor(status: number, statusText: string, body: unknown) {
    const code = readCode(body);
    // Le message porte le code métier quand il existe : c'est lui qui est utile
    // en log, pas « Request failed ».
    super(
      `API ${status}${code ? ` ${code}` : ""}${statusText ? ` — ${statusText}` : ""}`,
    );

    this.name = "ApiError";
    this.status = status;
    this.statusText = statusText;
    this.body = body;
    this.code = code;
    this.errorData = readErrorData(body);
  }

  /**
   * Normalise n'importe quelle valeur levée en `ApiError`.
   *
   * Trois formes peuvent arriver ici :
   *   - une `ApiError` (middleware de `useApiClient`) → renvoyée telle quelle ;
   *   - `{ status, statusText, body }` ;
   *   - le corps d'erreur brut `{ status, error, errorData }`, levé par les
   *     appelants de `useLazyApiClient`, qui n'a pas de middleware.
   */
  static from(error: unknown): ApiError | null {
    if (error instanceof ApiError) return error;
    if (!error || typeof error !== "object") return null;

    const candidate = error as {
      status?: unknown;
      statusText?: unknown;
      body?: unknown;
      error?: unknown;
    };

    const status =
      typeof candidate.status === "number" ? candidate.status : undefined;

    // Forme `{ status, statusText, body }` : le corps est dans `body`.
    if ("body" in candidate) {
      return new ApiError(
        status ?? 0,
        typeof candidate.statusText === "string" ? candidate.statusText : "",
        candidate.body,
      );
    }

    // Corps d'erreur brut : l'objet EST le corps.
    if (status !== undefined || typeof candidate.error === "string") {
      return new ApiError(status ?? 0, "", candidate);
    }

    return null;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

function readCode(body: unknown): string | undefined {
  if (!body || typeof body !== "object") return undefined;
  const code = (body as { error?: unknown }).error;
  return typeof code === "string" ? code : undefined;
}

function readErrorData(body: unknown): ApiErrorData {
  if (!body || typeof body !== "object") return undefined;
  const data = (body as { errorData?: unknown }).errorData;
  return Array.isArray(data) ? (data as ApiErrorData) : undefined;
}
